import { Controller } from "@hotwired/stimulus"

// Controller for displaying media usage details
export default class extends Controller {
  static targets = ["modal", "pagesList"]

  connect() {
    // Initialize modal state
    this.isOpen = false
  }

  // Show usage details modal
  showDetails(event) {
    event.preventDefault()

    const blobId = event.params.blobId
    const pagesData = event.params.pages

    if (!pagesData || pagesData.length === 0) {
      return
    }

    // Clear and populate pages list
    if (this.hasPagesListTarget) {
      this.pagesListTarget.innerHTML = ''

      // Parse pages data if it's a string
      let pages = pagesData
      if (typeof pagesData === 'string') {
        try {
          pages = JSON.parse(pagesData)
        } catch (e) {
          console.error('Failed to parse pages data:', e)
          return
        }
      }

      // Create list items for each usage record (Page, Project, Task, Objective, etc.)
      pages.forEach(item => {
        const listItem = document.createElement('li')
        listItem.className = 'py-2 px-3 hover:bg-neutral-50'

        const link = document.createElement('a')

        // Handle both old format (just Pages) and new format (with type)
        if (item.type) {
          // New format with type information
          switch(item.type) {
            case 'Page':
              // Pages use slug, fall back to id if slug is missing
              const pageIdentifier = item.slug || item.id
              link.href = `/admin/pages/${encodeURIComponent(pageIdentifier)}`
              break
            case 'Project':
              link.href = `/projects/${item.id}`
              break
            case 'Workspace':
              link.href = `/workspaces/${item.id}`
              break
            case 'Objective':
              link.href = `/objectives/${item.id}`
              break
            case 'Task':
              // Tasks use flat route
              link.href = `/tasks/${item.id}`
              break
            case 'User':
              link.href = `/admin/users/${item.id}`
              break
            default:
              // Unknown type - show text but no link
              link.href = '#'
              console.warn(`Unknown media usage type: ${item.type}`)
          }
        } else {
          // Old format (backward compatibility) - assume it's a Page
          // Use slug if available, otherwise fall back to id
          const identifier = item.slug || item.id
          link.href = `/admin/pages/${encodeURIComponent(identifier)}`
        }

        link.className = 'flex items-center justify-between text-sm'
        link.target = '_blank'

        const titleContainer = document.createElement('div')
        titleContainer.className = 'flex flex-col'

        const titleSpan = document.createElement('span')
        titleSpan.className = 'font-medium text-neutral-900'
        titleSpan.textContent = item.title || 'Untitled'

        titleContainer.appendChild(titleSpan)

        // Add type badge if available
        if (item.type) {
          const typeBadge = document.createElement('span')
          typeBadge.className = 'text-xs text-neutral-500'
          typeBadge.textContent = item.type
          titleContainer.appendChild(typeBadge)
        }

        const arrowSvg = this.createArrowIcon()

        link.appendChild(titleContainer)
        link.appendChild(arrowSvg)
        listItem.appendChild(link)

        this.pagesListTarget.appendChild(listItem)
      })
    }

    // Show modal
    this.showModal()
  }

  showModal() {
    if (this.hasModalTarget) {
      this.modalTarget.classList.remove('hidden')
      this.isOpen = true

      // Prevent body scroll
      document.body.style.overflow = 'hidden'
    }
  }

  hideModal(event) {
    if (event) {
      event.preventDefault()
    }

    if (this.hasModalTarget) {
      this.modalTarget.classList.add('hidden')
      this.isOpen = false

      // Restore body scroll
      document.body.style.overflow = ''
    }
  }

  // Create arrow icon for links
  createArrowIcon() {
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
    svg.setAttribute('class', 'h-4 w-4 text-neutral-400')
    svg.setAttribute('fill', 'none')
    svg.setAttribute('stroke', 'currentColor')
    svg.setAttribute('viewBox', '0 0 24 24')

    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path')
    path.setAttribute('stroke-linecap', 'round')
    path.setAttribute('stroke-linejoin', 'round')
    path.setAttribute('stroke-width', '2')
    path.setAttribute('d', 'M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14')

    svg.appendChild(path)
    return svg
  }

  // Handle escape key
  disconnect() {
    if (this.isOpen) {
      this.hideModal()
    }
  }
}